import CryptoJS from "crypto-es";

const BYTE_SIZE = 256;

function calcWord(num: number[]): number {
  return num[0] * 256 * 256 * 256 + num[1] * 256 * 256 + num[2] * 256 + num[3];
}

function bytesToWordArray(bytes: number[]): CryptoJS.lib.WordArray {
  let num = [0, 0, 0, 0];
  const result: number[] = [];
  for (let i = 0; i < bytes.length; i++) {
    const mod = i % 4;
    num[mod] = bytes[i];
    if (3 == mod) {
      result.push(calcWord(num));
      num = [0, 0, 0, 0];
    }
  }
  if (0 != bytes.length % 4) {
    result.push(calcWord(num));
  }
  return CryptoJS.lib.WordArray.create(result, bytes.length);
}

function wordArrayToUint8Array(wordArray: CryptoJS.lib.WordArray): Uint8Array {
  const sigBytes = wordArray.sigBytes;
  const bytes = new Uint8Array(sigBytes);
  for (let i = 0; i < sigBytes; i++) {
    const bite = (wordArray.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    bytes[i] = bite;
  }
  return bytes;
}

function encodeNumber(num: number, precision: number): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < precision; i++) {
    bytes.unshift(num % BYTE_SIZE);
    num = Math.floor(num / BYTE_SIZE);
  }
  return bytes;
}

function getKey(fileKey: Uint8Array): number[] {
  const key: number[] = [];
  for (let j = 0; j < 16; j++) {
    key[j] = fileKey[j] ^ fileKey[j + 16];
  }
  return key;
}

function getIv(fileKey: Uint8Array, blockIndex: number): number[] {
  const suffix: number[] = encodeNumber(blockIndex, 8);
  const iv: number[] = Array.from(fileKey.slice(16, 24)).concat(suffix);
  return iv;
}

function decodeFileKey(fileKey: string): Uint8Array {
  return wordArrayToUint8Array(CryptoJS.enc.Base64url.parse(fileKey));
}

export type Request = (startPos: number, endPos: number) => Promise<ArrayBuffer>;

export async function requestPlain(
  megaDecryptor: MegaDecryptor,
  originalRequest: Request,
  startPos: number,
  endPos: number,
  maxLength: number
): Promise<Uint8Array> {
  // 获取数据只能是一块一块的，但是range请求是连续的，所以需要分块请求
  // 获取数据快向下取整
  const blockIndex = Math.floor(startPos / 16);
  const minStartPos = 16 * blockIndex;
  // 获取数据快向上取整
  const maxEndPos = Math.min(16 * Math.ceil(endPos / 16), maxLength);
  // 添加延迟
  await new Promise(resolve => setTimeout(resolve, 500));
  const bytes = await originalRequest(minStartPos, maxEndPos);
  const cipherArray = new Uint8Array(bytes);
  const plain = megaDecryptor.decryptBlock(
    blockIndex,
    Array.from(cipherArray) as any
  );
  
  // 裁掉前后对齐偏移
  const offset = startPos - minStartPos;
  const result = plain.slice(offset, offset + (endPos - startPos) + 1);
  console.log("对齐偏移: ", offset, endPos, offset + (endPos - startPos) + 1);
  return result;
}

export class MegaDecryptor {
  key: CryptoJS.lib.WordArray;
  fileKey: Uint8Array;
  constructor(fileKey: string) {
    const fileKeyBytes = decodeFileKey(fileKey);
    this.fileKey = fileKeyBytes;
    const key = getKey(fileKeyBytes);
    this.key = bytesToWordArray(key);
  }
  /**
   * 解密目标块
   * @param blockIndex 数据块的索引
   * @param cipher 16字节的字节数组密文
   */
  decryptBlock(blockIndex: number, cipher: number[]) {
    if (!cipher || cipher.length === 0) {
      throw new Error("Cipher data is empty");
    }
    const iv = bytesToWordArray(getIv(this.fileKey, blockIndex));
    // 在 CTR 模式下，加密和解密在数学上是相同的操作（都是 XOR）
    // 但 CryptoJS 的 decrypt 方法在 CTR 模式下能正确处理密文输入
    try {
      const plain = CryptoJS.AES.decrypt(
        CryptoJS.lib.CipherParams.create({
          ciphertext: bytesToWordArray(cipher),
        }),
        this.key,
        {
          iv: iv,
          mode: CryptoJS.mode.CTR,
          padding: CryptoJS.pad.NoPadding,
        }
      );
      const result = wordArrayToUint8Array(plain);
      return result;
    } catch (error) {
      console.error("Decryption error:", error, "blockIndex:", blockIndex, "cipher length:", cipher.length);
      throw error;
    }
  }

  decryptCTR(cipher: Uint8Array, blockIndex: number): Uint8Array {
    const iv = bytesToWordArray(getIv(this.fileKey, blockIndex));

    const plain = CryptoJS.AES.decrypt(
      CryptoJS.lib.CipherParams.create({
        ciphertext: bytesToWordArray(Array.from(cipher)),
      }),
      this.key,
      {
        iv,
        mode: CryptoJS.mode.CTR,
        padding: CryptoJS.pad.NoPadding,
      }
    );

    return wordArrayToUint8Array(plain);
  }
}

/**
 * 获取上传文件，用于手动触发一个文件上传弹出框不需要写file文件域
 * @param {Object} cb 回调函数，返回包含所有选择文件的FileList对象
 */
chooseFile.lastDom = undefined as undefined | HTMLInputElement;
export function chooseFile(
  cb: (files: FileList) => void,
  opt?: {
    accept?: string;
  }
) {
  opt = opt || {};
  const _self = chooseFile;
  if (_self.lastDom) {
    //如果上一次上传用户点击了取消，则没有执行change回调，把上一次生成的节点移除掉
    document.body.removeChild(_self.lastDom);
    _self.lastDom = undefined;
  }
  const fileDom = document.createElement("input");
  fileDom.style.display = "none";
  fileDom.setAttribute("type", "file");
  if (opt.accept) {
    fileDom.setAttribute("accept", opt.accept);
  }
  fileDom.addEventListener("change", function () {
    _self.lastDom = undefined;
    "function" === typeof cb && cb(fileDom.files!);
    document.body.removeChild(fileDom);
  });
  //如果不添加到文档里，IE下不能弹出选择文件的对话框
  document.body.appendChild(fileDom);
  _self.lastDom = fileDom;
  fileDom.click();
}

// function test() {
//   const message = CryptoJS.lib.WordArray.create([
//     0x00010203,
//     0x04050607,
//     0x08090a0b,
//     0x0c0d0e0f, //
//     0x10111213,
//     0x14151617,
//     0x18191a1b,
//     0x1c1d1e1f,
//   ]);
//   const key = CryptoJS.lib.WordArray.create([
//     0x20212223, 0x24252627, 0x28292a2b, 0x2c2d2e2f,
//   ]);
//   const iv = CryptoJS.lib.WordArray.create([
//     0x30313233, 0x34353637, 0x38393a3b, 0x3c3d3e3f,
//   ]);
//   const encrypted = CryptoJS.AES.encrypt(message, key, {
//     iv: iv,
//     mode: CryptoJS.mode.CTR,
//     padding: CryptoJS.pad.NoPadding,
//   });
//   const ciphertext = wordArrayToUint8Array(encrypted.ciphertext);
//   const second = ciphertext.slice(16);
//   const plain = decryptBlock(
//     key,
//     wordArrayToUint8Array(iv) as any,
//     1,
//     second as any
//   );
//   console.log(plain);
// }
